import { createContext, useCallback, useContext, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'

const SheetCtx = createContext<() => void>(() => {})
/** The animated close, for a Done button inside a Sheet. */
export const useSheetClose = () => useContext(SheetCtx)

/**
 * Scrim plus sheet with motion (F2). Bottom sheets slide up and can be dragged down to dismiss; centred pickers scale in.
 * Closing keeps the sheet mounted for the exit, then calls onClose. If the caller refuses the close (its guard says busy),
 * the sheet is still mounted afterwards and slides back into place. Escape and confirm paths call onClose directly, as before.
 */
export function Sheet({ onClose, center, picker, width, style, label, className, children }: {
  onClose: () => void; center?: boolean; picker?: boolean; width?: number; style?: CSSProperties; label?: string; className?: string; children: ReactNode
}) {
  const [closing, setClosing] = useState(false)
  const [drag, setDrag] = useState<{ y0: number; dy: number; t0: number } | null>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(timer.current), [])

  const close = useCallback(() => {
    if (closing) return
    setClosing(true); setDrag(null)
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      onClose()
      // still mounted after onClose means the caller refused (busy): slide back
      window.requestAnimationFrame(() => { if (sheetRef.current) setClosing(false) })
    }, 160)
  }, [closing, onClose])

  // drag to dismiss, from the handle only, bottom sheets only
  const onDown = (e: ReactPointerEvent) => {
    if (center) return
    e.currentTarget.setPointerCapture?.(e.pointerId)
    setDrag({ y0: e.clientY, dy: 0, t0: Date.now() })
  }
  const onMove = (e: ReactPointerEvent) => { if (drag) setDrag({ ...drag, dy: Math.max(0, e.clientY - drag.y0) }) }
  const onUp = () => {
    if (!drag) return
    const fast = drag.dy > 24 && Date.now() - drag.t0 < 250
    if (drag.dy > 90 || fast) close(); else setDrag(null)
  }

  const sheetStyle: CSSProperties = { ...(width ? { width } : {}), ...style, ...(drag ? { transform: `translateY(${drag.dy}px)` } : {}) }
  return (
    <div className={'scrim' + (center ? ' scrim--center' : '') + (closing ? ' closing' : '')} onClick={close}>
      <div ref={sheetRef} className={'sheet' + (picker ? ' sheet--picker' : '') + (drag ? ' dragging' : '') + (className ? ' ' + className : '')} role="dialog" aria-label={label} style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        {!center && <div className="sheet__handle" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={() => setDrag(null)}><div className="sheet__grab" /></div>}
        <SheetCtx.Provider value={close}>{children}</SheetCtx.Provider>
      </div>
    </div>
  )
}
